import React, { useCallback, useContext, useRef } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { StyledTimelinePanelWrapper } from "./styled";
import { openAdvancedSettings } from "../../_actions/sidebarSliderActions";
import { TimelineContext } from "../../_utils/timeline";
import TimelineComponent from "./TimelineComponent";
import PlaybackButtons from "./PlaybackButtons";
import DurationIndicator from "./DurationIndicator";
import { DURATION_PIXEL_RATIO } from "../../_components/styled/variable";

import { ReactComponent as MotionIcon } from "../../assets/icons/motion.svg";
import {
  ADVANCED_ANIMATION_PANEL,
  ADVANCED_EDITOR_EDIT,
} from "../../_components/details/constants";
import {
  closeBottomPanel,
  openBottomPanel,
} from "../../_actions/bottomPanelActions";
import { BottomPanelViewTypes } from "../../_utils/constants";
import { selectArtboard } from "../../_actions/motionActions";
import LayerTimelineComponent from "./LayerTimelineComponent";
import { StyledLayerTimelineComponentContainer } from "../../_components/styled/details/styleArtBoardEditor";
import { IconButton, Tooltip } from "@chakra-ui/react";
import {
  FaArrowLeft,
  FaCompress,
  FaExpand,
  FaGripHorizontal,
  FaTimes,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const TimelineEditor = ({ canvasRef }) => {
  const { t } = useTranslation(["studio"]);
  const { duration, currentTime, changeTimescale, timescale, seek } =
    useContext(TimelineContext);
  const titlebarRef = useRef(null);

  const selectedArtboard = useSelector(
    (state) => state.motionStore?.selectedArtboard
  );
  const dispatch = useDispatch();

  const closeBottomPanelAction = useCallback(
    () => dispatch(closeBottomPanel()),
    [dispatch]
  );

  const selectArtboardAction = useCallback(
    () => dispatch(selectArtboard()),
    [dispatch]
  );

  const openAdvancedSettingsAction = useCallback(
    (panel, selectedTab) => dispatch(openAdvancedSettings(panel, selectedTab)),
    [dispatch]
  );
  const openBottomPanelAction = useCallback(
    (panelType) => dispatch(openBottomPanel(panelType)),
    [dispatch]
  );

  const closeTimelinePanel = () => {
    batch(() => {
      closeBottomPanelAction();
      selectArtboardAction();
    });
  };

  const toggleTimelineView = () => {
    if (titlebarRef.current) {
      if (timescale === 1) {
        const barWidth = selectedArtboard?.id
          ? titlebarRef.current.offsetWidth - 200
          : titlebarRef.current.offsetWidth;
        const expectedRatio =
          (barWidth - 16) / (duration * DURATION_PIXEL_RATIO);
        changeTimescale(expectedRatio);
      } else {
        changeTimescale(1);
      }
    }
  };

  const openAnimationPanel = () => {
    openAdvancedSettingsAction(ADVANCED_ANIMATION_PANEL, ADVANCED_EDITOR_EDIT);
  };

  const onClickFullScreenPreview = () => {
    openBottomPanelAction(BottomPanelViewTypes.ARTBOARDS_GRID_VIEW);
  };

  const detectDataClickAndCloseBottomPanel = (e) => {
    if (e.target?.dataset?.click) {
      closeTimelinePanel();
    }
  };

  return (
    <StyledTimelinePanelWrapper>
      <div
        id="timeline-titlebar"
        className="title-bar"
        data-click="true"
        ref={titlebarRef}
        onClick={(e) => {
          if (e.target.id === "timeline-titlebar") closeTimelinePanel();
        }}
      >
        <div
          className="left-actions"
          data-click="true"
          onClick={detectDataClickAndCloseBottomPanel}
        >
          {selectedArtboard?.id && (
            <Tooltip placement="top" hasArrow label={t("back_to_all")}>
              <IconButton
                icon={<FaArrowLeft />}
                variant="ghost"
                size="sm"
                onClick={() => {
                  seek(0);
                  selectArtboardAction();
                }}
                aria-label="arrow-icon"
              />
            </Tooltip>
          )}
          <DurationIndicator total={duration} current={currentTime} />
        </div>
        <PlaybackButtons></PlaybackButtons>
        <div
          className="right-actions"
          data-click="true"
          onClick={detectDataClickAndCloseBottomPanel}
        >
          <Tooltip placement="top" hasArrow label={t("arrange")}>
            <IconButton
              icon={<FaGripHorizontal />}
              variant="ghost"
              size="sm"
              onClick={onClickFullScreenPreview}
              aria-label="icon-grip"
            />
          </Tooltip>

          <Tooltip
            hasArrow
            placement="top"
            label={timescale === 1 ? t("fit_view") : t("reset_view")}
          >
            <IconButton
              icon={timescale === 1 ? <FaExpand /> : <FaCompress />}
              variant="ghost"
              size="sm"
              onClick={toggleTimelineView}
              display={["none", "flex"]}
              aria-label="icon-expand-compress"
            />
          </Tooltip>
          <Tooltip hasArrow placement="top" label={t("open_preset")}>
            <IconButton
              display={["none", "none", "inline-block"]}
              onClick={openAnimationPanel}
              variant="ghost"
              size="sm"
              icon={<MotionIcon height="1em" />}
              aria-label="motion-icon"
            />
          </Tooltip>
          <Tooltip placement="top" hasArrow label={t("close_editor")}>
            <IconButton
              variant="ghost"
              size="sm"
              icon={<FaTimes />}
              onClick={closeTimelinePanel}
              aria-label="icon-times"
            />
          </Tooltip>
        </div>
      </div>
      {selectedArtboard?.id ? (
        <StyledLayerTimelineComponentContainer>
          <LayerTimelineComponent />
        </StyledLayerTimelineComponentContainer>
      ) : (
        <TimelineComponent canvasRef={canvasRef} />
      )}
    </StyledTimelinePanelWrapper>
  );
};

TimelineEditor.propTypes = {};

export default TimelineEditor;
